import React, { useEffect, useRef, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import { candidacyService } from '@/shared/services/candidacyService';
import {
    WITHDRAWABLE_STAGES,
    candidaciesForProfile,
} from '@/shared/utils/candidacyStage';
import StatusPill from '@/components/common/StatusPill';
import LoadingState from '@/components/common/LoadingState';
import LoadError from '@/components/common/LoadError';
import { meetingLines, positionHeading } from '@/shared/utils/positionText';
import {
    keyDateOf,
    positionCountsText,
    groupPositions,
    positionShortName,
    STAGE_LEGEND,
} from '@/shared/utils/studentPositions';
import CandidacyOfferCard, { formatDate } from './CandidacyOfferCard';
import InterviewBlock from './InterviewBlock';
import useAcceptInfo from './useAcceptInfo';
import WithdrawConfirmModal from './WithdrawConfirmModal';
import './StudentPositions.css';

const ANSWERABLE_STAGES = ['offered', 'accepted', 'declined'];
// A student who just left an accepted place keeps the offer card, with its
// result, so `released` mounts it too. A released candidacy offers no action.
// An expired offer keeps its card too: it says the offer ended and offers no
// action.
const OFFER_STAGES = [...ANSWERABLE_STAGES, 'expired', 'released'];
const UNLISTED = 'A position that is no longer listed';

const headingOf = (candidacy) =>
    candidacy.position ? positionHeading(candidacy.position) : UNLISTED;

function StageBadge({ stage }) {
    return <StatusPill family="candidacy" value={stage} />;
}

function FullItem({
    candidacy,
    commitment,
    isLocked,
    onWithdraw,
    onChange,
    onReload,
    headingRef,
    acceptInfo,
    otherOffers,
}) {
    const { position } = candidacy;
    const heading = headingOf(candidacy);
    const meets = meetingLines(position?.meetings);
    // The offer card carries the instructor and meetings itself.
    const cardShown = OFFER_STAGES.includes(candidacy.stage);

    return (
        <li className="mb-4">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                <h3 className="h5 mb-0" tabIndex={-1} ref={headingRef}>
                    {heading}
                </h3>
                <StageBadge stage={candidacy.stage} />
            </div>
            {candidacy.createdAt && (
                <p className="mb-1 text-body-secondary">
                    Applied {formatDate(candidacy.createdAt)}
                </p>
            )}
            {!cardShown && position?.instructorName && (
                <p className="mb-1">Instructor: {position.instructorName}</p>
            )}
            {!cardShown && meets.length > 0 && (
                <ul className="list-unstyled mb-1">
                    {meets.map((line) => (
                        <li key={line}>{line}</li>
                    ))}
                </ul>
            )}
            <InterviewBlock candidacy={candidacy} />
            {(candidacy.feedback || []).map((entry, index) => (
                <p className="mb-1" key={`${entry.at}-${index}`}>
                    From the reviewers, {formatDate(entry.at)}: {entry.text}
                </p>
            ))}
            {cardShown && (
                <CandidacyOfferCard
                    candidacy={candidacy}
                    commitment={commitment}
                    onAnswered={onChange}
                    onChanged={onReload}
                    locked={isLocked}
                    acceptInfo={acceptInfo}
                    otherOffers={otherOffers}
                />
            )}
            {WITHDRAWABLE_STAGES.includes(candidacy.stage) && (
                <Button
                    variant="outline-danger"
                    size="sm"
                    className="mt-2"
                    disabled={isLocked}
                    aria-describedby={
                        isLocked ? PREVIEW_LOCK_NOTE_ID : undefined
                    }
                    onClick={() => onWithdraw(candidacy)}
                >
                    Withdraw from this position
                    <span className="visually-hidden"> {heading}</span>
                </Button>
            )}
        </li>
    );
}

function PositionRow({
    candidacy,
    commitment,
    isLocked,
    onChange,
    onReload,
    open,
    onToggle,
    acceptInfo,
    otherOffers,
}) {
    const { position } = candidacy;
    const offered = candidacy.stage === 'offered';
    const keyDate = keyDateOf(candidacy);
    const panelId = `offer-${candidacy.id}`;
    const title = position?.title;
    const cardShown =
        OFFER_STAGES.includes(candidacy.stage) && (!offered || open);

    return (
        <li id={`position-${candidacy.id}`} className="py-2 border-bottom">
            <div className="d-flex flex-wrap align-items-center gap-2 row-gap-1">
                <div className="flex-grow-1 me-2">
                    <span className="fw-semibold">
                        {position ? positionShortName(position) : UNLISTED}
                    </span>
                    {title && (
                        <span className="d-block small text-body-secondary">
                            {title}
                        </span>
                    )}
                </div>
                <StageBadge stage={candidacy.stage} />
                {keyDate && (
                    <span className="small text-body-secondary">{keyDate}</span>
                )}
                {offered && (
                    <Button
                        variant="primary"
                        size="sm"
                        className="ms-sm-auto position-action"
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={onToggle}
                    >
                        {open ? 'Hide offer' : 'Review offer'}
                        <span className="visually-hidden">
                            {' '}
                            for {headingOf(candidacy)}
                        </span>
                    </Button>
                )}
            </div>
            <InterviewBlock candidacy={candidacy} />
            <div id={panelId}>
                {cardShown && (
                    <CandidacyOfferCard
                        candidacy={candidacy}
                        commitment={commitment}
                        onAnswered={onChange}
                        onChanged={onReload}
                        locked={isLocked}
                        acceptInfo={acceptInfo}
                        otherOffers={otherOffers}
                    />
                )}
            </div>
        </li>
    );
}

/**
 * The student's position applications, one item each. `full` is the page's
 * list with the details and a withdraw for each position that can still be
 * withdrawn from; `compact` is a line per position for the student's card.
 * `onChange(candidacy)` receives the server's version of a candidacy after a
 * withdraw or an answer to an offer. Left unset, `locked` follows the role
 * preview and the list shows the note; a parent that sets it shows the note.
 */
function PositionApplicationsList({
    candidacies,
    commitments = {},
    variant = 'full',
    onChange,
    onReload,
    locked,
    acceptInfo,
    openOfferId,
    onOpenOfferChange,
}) {
    const previewLock = usePreviewLock();
    const isLocked = locked ?? previewLock;
    const [ownOpenId, setOwnOpenId] = useState(null);
    const openId = openOfferId === undefined ? ownOpenId : openOfferId;
    const setOpenId = onOpenOfferChange || setOwnOpenId;
    const openOffers = candidacies.filter((c) => c.stage === 'offered');
    const [pending, setPending] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const [focusId, setFocusId] = useState(null);
    const headingRefs = useRef({});

    useEffect(() => {
        if (focusId) {
            headingRefs.current[focusId]?.focus();
            setFocusId(null);
        }
    }, [focusId]);

    const withdraw = async () => {
        const updated = await candidacyService.withdraw(pending.id);
        onChange?.(updated);
        setAnnouncement(`You withdrew from ${headingOf(pending)}.`);
        setFocusId(pending.id);
        setPending(null);
    };

    const grouped = groupPositions(candidacies);
    const shown = variant === 'compact' ? grouped.current : candidacies;
    const past = grouped.past;
    const pendingHeading = pending ? headingOf(pending) : '';
    const actionable = candidacies.some((c) =>
        [...ANSWERABLE_STAGES, ...WITHDRAWABLE_STAGES].includes(c.stage)
    );

    return (
        <>
            {announcement && (
                <p role="status" className="mb-2 fw-semibold">
                    {announcement}
                </p>
            )}
            <ul className="list-unstyled mb-0">
                {shown.map((candidacy) =>
                    variant === 'compact' ? (
                        <PositionRow
                            key={candidacy.id}
                            candidacy={candidacy}
                            commitment={commitments[candidacy.term]}
                            isLocked={isLocked}
                            onChange={onChange}
                            onReload={onReload}
                            open={openId === candidacy.id}
                            onToggle={() =>
                                setOpenId(
                                    openId === candidacy.id
                                        ? null
                                        : candidacy.id
                                )
                            }
                            acceptInfo={acceptInfo}
                            otherOffers={
                                openOffers.filter((o) => o.id !== candidacy.id)
                                    .length
                            }
                        />
                    ) : (
                        <FullItem
                            key={candidacy.id}
                            candidacy={candidacy}
                            commitment={commitments[candidacy.term]}
                            isLocked={isLocked}
                            onWithdraw={setPending}
                            onChange={onChange}
                            onReload={onReload}
                            acceptInfo={acceptInfo}
                            otherOffers={
                                openOffers.filter((o) => o.id !== candidacy.id)
                                    .length
                            }
                            headingRef={(element) => {
                                headingRefs.current[candidacy.id] = element;
                            }}
                        />
                    )
                )}
            </ul>
            {variant === 'compact' && past.length > 0 && (
                <details className="mt-2 sap-details">
                    <summary className="fw-semibold">
                        Past outcomes ({past.length})
                    </summary>
                    <ul className="list-unstyled mb-0">
                        {past.map((candidacy) => (
                            <PositionRow
                                key={candidacy.id}
                                candidacy={candidacy}
                                commitment={commitments[candidacy.term]}
                                isLocked={isLocked}
                                onChange={onChange}
                                onReload={onReload}
                                open={false}
                                onToggle={() => {}}
                                acceptInfo={acceptInfo}
                                otherOffers={0}
                            />
                        ))}
                    </ul>
                </details>
            )}
            {locked === undefined && actionable && (
                <PreviewLockNote locked={isLocked} />
            )}
            {variant === 'full' && (
                <WithdrawConfirmModal
                    show={Boolean(pending)}
                    title={`Withdraw from ${pendingHeading}?`}
                    body={
                        <p className="mb-0">
                            Reviewers stop considering you for this position.
                            While it is open you can resubmit from Open
                            Positions.
                        </p>
                    }
                    confirmLabel="Withdraw"
                    onConfirm={withdraw}
                    onCancel={() => setPending(null)}
                />
            )}
        </>
    );
}

/**
 * What the student's card on the applications page shows for one student
 * application: its positions as rows, what the stages mean, and a link to the
 * application's own page.
 */
export function StudentCardPositions({
    application,
    myCandidacies,
    openOfferId,
    onOpenOfferChange,
}) {
    const { candidacies, commitments, loading, error, replace, reload } =
        myCandidacies;
    const programLabel = application.program?.toUpperCase();
    const forProfile = candidaciesForProfile(candidacies, application);
    const acceptInfo = useAcceptInfo(
        application,
        forProfile.some((c) => ['offered', 'accepted'].includes(c.stage))
    );

    return (
        <div className="mt-3 pt-3 border-top">
            <h3 className="h6 mb-0">Positions you applied to</h3>
            {!loading && !error && forProfile.length > 0 && (
                <p className="small text-body-secondary mb-1">
                    {positionCountsText(forProfile)}
                </p>
            )}
            {loading ? (
                <LoadingState rows={1} label="Loading your positions" />
            ) : error ? (
                <LoadError
                    message="Your position applications could not be loaded."
                    onRetry={reload}
                    className="mb-2"
                />
            ) : forProfile.length > 0 ? (
                <>
                    <PositionApplicationsList
                        candidacies={forProfile}
                        commitments={commitments}
                        variant="compact"
                        onChange={replace}
                        onReload={reload}
                        acceptInfo={acceptInfo}
                        openOfferId={openOfferId}
                        onOpenOfferChange={onOpenOfferChange}
                    />
                </>
            ) : (
                <p className="mb-2">
                    You haven&apos;t applied to any positions yet.
                </p>
            )}
            <Link to={`/my-applications/${application._id}`}>
                View your {programLabel} application
            </Link>
        </div>
    );
}

export function StageLegend() {
    return (
        <details className="mt-4 sap-details">
            <summary className="fw-semibold">
                What do these statuses mean?
            </summary>
            <Card className="mt-2 bg-light border-0">
                <Card.Body>
                    <dl className="row mb-0">
                        {STAGE_LEGEND.map(([stage, text]) => (
                            <React.Fragment key={stage}>
                                <dt className="col-sm-3 mb-1">
                                    <StageBadge stage={stage} />
                                </dt>
                                <dd className="col-sm-9 mb-2">{text}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </Card.Body>
            </Card>
        </details>
    );
}

export default PositionApplicationsList;
